import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Flex, Spinner } from "@chakra-ui/react";

// @ts-ignore - Modal is a JSX component without TS declarations
import { Modal } from "ui-kit/modal/modal";
import { getUserData, getUserNames } from "store/selectors/user";

declare global {
  interface Window {
    referralJS?: Record<string, unknown>;
  }
}

const PROGRAM_ID = import.meta.env.VITE_REFERRALROCK_PROGRAM_ID as string | undefined;
const WIDGET_ID = "rr-share-widget";

interface ReferralRockModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberReady?: boolean;
}

const ReferralRockModal = ({ isOpen, onClose, memberReady }: ReferralRockModalProps) => {
  const user = useSelector(getUserData);
  const { first_name, last_name } = useSelector(getUserNames);
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  // Configure SDK to target the iframe widget only after the member is ready
  useEffect(() => {
    if (!memberReady || !PROGRAM_ID || !user.email) return;
    const fullName = `${first_name ?? ""} ${last_name ?? ""}`.trim();

    window.referralJS = window.referralJS ?? {};
    window.referralJS.access = {
      targetId: WIDGET_ID,
      parameters: {
        view: "iframe",
        programIdentifier: PROGRAM_ID,
        email: user.email,
        fullName
      }
    };
  }, [memberReady, user.email, first_name, last_name]);

  // Move iframe back to hidden container when modal closes
  useEffect(() => {
    if (!isOpen) {
      const widget = document.querySelector(`#${WIDGET_ID}`);
      if (widget && hiddenContainerRef.current) {
        hiddenContainerRef.current.append(widget);
      }
      setReady(false);
    }
  }, [isOpen]);

  // Callback ref: moves the widget into the modal body as soon as React mounts it
  const modalBodyRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const widget = document.querySelector(`#${WIDGET_ID}`);
    if (widget) {
      node.append(widget);
      setTimeout(() => setReady(true), 300);
    }
  }, []);

  return (
    <>
      {/* Hidden container keeps the iframe always in the DOM */}
      <div
        ref={hiddenContainerRef}
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", visibility: "hidden" }}
      >
        <iframe
          id={WIDGET_ID}
          title="Referral Rock Share Widget"
          style={{ width: "100%", minHeight: "430px", border: "none" }}
        />
      </div>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Refer a Friend"
        size="xl"
        bodyProps={{ p: 0, overflow: "hidden", borderBottomRadius: "md" }}
      >
        {!ready && (
          <Flex justify="center" align="center" minH="430px">
            <Spinner size="lg" color="blue.500" />
          </Flex>
        )}
        <Box
          ref={modalBodyRef}
          minH={ready ? "430px" : 0}
          height={ready ? "auto" : 0}
          overflow="hidden"
          borderBottomRadius="md"
        />
      </Modal>
    </>
  );
};

export default ReferralRockModal;
